import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Card from '@material-ui/core/Card';
import { Field, getFormValues } from 'redux-form';
import { withStyles } from '@material-ui/core/styles';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import withWidth from '@material-ui/core/withWidth';
import GoogleMapReact from 'google-map-react';

import styles from './styles';
import {
  FORMS,
  AUS_STATES,
  SERVICE_AREAS,
  GOOGLE_MAPS_API_KEY,
  LOCAL_SERVICE_AREA_RADIUS_KM,
  AUS_CENTER,
  SOUTH_POLE,
} from '../../../../constants';
import { required, coordRequired } from '../../../../utilities/validators';
import PlaceSearchFormField from '../../../../fields/reduxForm/PlaceSearchFormField';
import SelectFormField from '../../../../fields/reduxForm/SelectFormField';
import ServiceAreaButtons from '../../../../fields/reduxForm/ServiceAreaButtons';
import HiddenTextField from '../../../../fields/reduxForm/HiddenTextField';

const propTypes = {
  classes: PropTypes.shape({
    cardHeaderRoot: PropTypes.string,
    cardHeaderTitle: PropTypes.string,
    formGroup: PropTypes.string,
    halfFormGroup: PropTypes.string,
    formRow: PropTypes.string,
  }).isRequired,
  formValues: PropTypes.object,
  mapDefaultCenter: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  mapDefaultZoom: PropTypes.number,
  mapCenter: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  changeFieldValue: PropTypes.func.isRequired, // from redux-form
  touchField: PropTypes.func.isRequired, // from redux-form
  width: PropTypes.string.isRequired, // from Mui
};

const defaultProps = {
  formValues: {},
  mapDefaultCenter: AUS_CENTER,
  mapDefaultZoom: 4,
};

const mapCircleInitialCenter = [SOUTH_POLE.lat, SOUTH_POLE.lng];

export const FieldsGroupThree = props => {
  const {
    classes,
    formValues,
    mapDefaultCenter,
    mapDefaultZoom,
    changeFieldValue,
    touchField,
    width,
  } = props;
  const [cachedMap, setCachedMap] = useState();
  const [cachedMaps, setCachedMaps] = useState();
  const [cachedMapCircle, setCachedMapCircle] = useState();

  const correctZoom = zoomLevel => {
    let correctZoomLevel = zoomLevel;
    if (width === 'xs' || width === 'sm') {
      correctZoomLevel -= 1;
    } else if (width === 'md') {
      correctZoomLevel += 1;
    }
    return correctZoomLevel;
  };

  const resetLocalMap = () => {
    if (cachedMap && cachedMaps) {
      cachedMap.setCenter(mapDefaultCenter);
      cachedMap.setZoom(correctZoom(mapDefaultZoom));
      if (cachedMapCircle) {
        const newC = new cachedMaps.LatLng(mapCircleInitialCenter);
        cachedMapCircle.setCenter(newC);
      }
    }
  };

  const setLocalMap = latLong => {
    if (cachedMap && cachedMaps) {
      if (cachedMapCircle) {
        const newC = new cachedMaps.LatLng(latLong);
        cachedMapCircle.setCenter(newC);
        cachedMap.fitBounds(cachedMapCircle.getBounds());
        if (width === 'xs' || width === 'sm') {
          cachedMap.setZoom(9);
        }
      }
    }
  };

  const handleServiceAreaButtons = type => {
    switch (type) {
      case SERVICE_AREAS.LOCAL:
        handleLocal();
        break;
      case SERVICE_AREAS.STATEWIDE:
        handleStatewide();
        break;
      case SERVICE_AREAS.NATIONAL:
        handleNational();
        break;
      default:
    }
  };

  const handleNational = () => {
    changeFieldValue('custom:serviceAState', '');
    handleClearCoord();
    changeFieldValue('custom:serviceAAdd', '');
  };
  const handleStatewide = () => {
    resetLocalMap();
    handleClearCoord();
    changeFieldValue('custom:serviceAAdd', '');
    // reset drop down
  };
  const handleStateSelect = selectedState => {
    const state = AUS_STATES.filter(obj => obj.short_name === selectedState)[0];
    // move map to location in AUS_STATES
    if (cachedMap && state) {
      cachedMap.setCenter({
        lat: state.center_location.lat,
        lng: state.center_location.lng,
      });
      cachedMap.setZoom(correctZoom(state.zoom_level));
    } else if (!state) {
      resetLocalMap();
    }
  };
  const handleLocal = () => {
    changeFieldValue('custom:serviceAState', '');
    resetLocalMap();
  };

  const handleGettingCoord = latLong => {
    changeFieldValue(
      'custom:serviceAreaCoord',
      `${latLong.lat}, ${latLong.lng}`,
    );
    setLocalMap(latLong);
    changeFieldValue('custom:serviceAState', '');
  };

  const handleClearCoord = () => {
    changeFieldValue('custom:serviceAreaCoord', '');
    resetLocalMap();
  };

  const handleServiceAreaChange = () => {
    touchField('custom:serviceAreaCoord');
  };

  const getStateOptions = () => {
    const options = AUS_STATES.map(state => (
      <option value={state.short_name}>{state.long_name}</option>
    ));
    return options;
  };

  const getCircleProps = (map, center) => ({
    strokeColor: '#979797',
    strokeOpacity: 1,
    strokeWeight: 0.7,
    fillColor: '#009a44',
    fillOpacity: 0.15,
    map,
    center,
    radius: LOCAL_SERVICE_AREA_RADIUS_KM * 1000,
  });

  const newMapCircle = (map, maps, center) =>
    new maps.Circle(getCircleProps(map, center));

  const apiIsLoaded = (map, maps) => {
    if (map) {
      map.set('disableDefaultUI', true);
      map.set('gestureHandling', 'none');
      map.set('zoomControl', false);

      setCachedMap(map);
      setCachedMaps(maps);

      setCachedMapCircle(newMapCircle(map, maps, mapCircleInitialCenter));
    }
  };

  return (
    <Card>
      <CardHeader
        classes={{
          root: classes.cardHeaderRoot,
          title: classes.cardHeaderTitle,
        }}
        title="Step 3: Service Areas"
      />
      <CardContent className={classes.serviceAreasCardContent}>
        <div className={classes.serviceAreaContain}>
          <div className={classes.serviceAreaForm}>
            <Typography variant="h6">Locations</Typography>
            <Typography variant="body1">Please select</Typography>
            <div
              className={classes.formRow}
              data-test="business-address-field-group"
            >
              <div className={classes.formGroup}>
                <Field
                  name="custom:serviceAType"
                  classes={classes}
                  component={ServiceAreaButtons}
                  validate={[required]}
                  onChange={handleServiceAreaButtons}
                />

                {formValues['custom:serviceAType'] ===
                  SERVICE_AREAS.STATEWIDE && (
                  <div className={classes.stateSelectContain}>
                    <Field
                      classes={classes}
                      name="custom:serviceAState"
                      component={SelectFormField}
                      label="State"
                      validate={[required]}
                      props={{
                        id: 'service-area-state-select-field',
                        label: 'Select State',
                        onSelectChange: handleStateSelect,
                        onClear: handleClearCoord,
                        onBlur: handleServiceAreaChange,
                      }}
                    >
                      <option />
                      {getStateOptions()}
                    </Field>
                  </div>
                )}
                {formValues['custom:serviceAType'] === SERVICE_AREAS.LOCAL && (
                  <>
                    <Typography variant="body">
                      Type address for {LOCAL_SERVICE_AREA_RADIUS_KM.toString()}
                      km radius
                    </Typography>
                    <Field
                      name="custom:serviceAAdd"
                      component={PlaceSearchFormField}
                      props={{
                        id: 'service-area-address-text-field',
                        label: 'Service Area Address',
                        fullWidth: true,
                        onGettingCoord: handleGettingCoord,
                        onClearCoord: handleClearCoord,
                        onFieldBlur: handleServiceAreaChange,
                      }}
                    />
                    <div className={classes.serviceAreaCoord}>
                      <Field
                        name="custom:serviceAreaCoord"
                        component={HiddenTextField}
                        props={{
                          id: 'service-area-coord-text-field',
                          fullWidth: true,
                        }}
                        validate={[coordRequired]}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={classes.serviceAreaMapContain}>
            <div className={classes.serviceAreaMap}>
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: GOOGLE_MAPS_API_KEY,
                }}
                defaultCenter={mapDefaultCenter}
                defaultZoom={correctZoom(mapDefaultZoom)}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

FieldsGroupThree.propTypes = propTypes;
FieldsGroupThree.defaultProps = defaultProps;

const mapStateToProps = state => ({
  formValues: getFormValues(FORMS.REGISTRATION_LONG)(state),
});

export default withStyles(styles)(
  connect(mapStateToProps)(withWidth()(FieldsGroupThree)),
);
